import { useCallback, useState } from 'react';
import { Color, GEOLOCATION_REFUSAL_KEYS, Size, locationPinPath, mapsSearchHref, t, type DateFormat } from '@bms2/shared';
import { Button } from '../../components/controls';
import { del, post } from './api';
import { readPosition, type GeolocationReader, browserGeolocation } from './geolocation';
import { whenText } from '../../shell/viewerDates';
import styles from './RecordPage.module.css';

/**
 * A Location's pin (decision 0674): the point a person placed from where they
 * stand or read from a pasted Maps link (decision 1600), who placed it and
 * when, with Pin here and Clear pin. 🔨 Pin here reads
 * the browser's own location after its consent prompt; a refused or failed
 * reading writes nothing and names why. The time is drawn in the viewer's zone.
 */

export interface LocationPinFacts {
  pinLine: string | null;
  pinMapsQuery: string | null;
  pinnedBy: string | null;
  pinnedAt: string | null;
  /** The Google Maps link the pin was read from, where a person pasted one (decision 1600). */
  pinnedFromLink?: string | null;
}

export interface LocationPinPanelProps {
  locationId: string;
  facts: LocationPinFacts;
  zone: string;
  dateFormat: DateFormat;
  apiBaseUrl?: string;
  /** Whether the viewer may write the Location; a reader sees the pin and no controls. */
  editable: boolean;
  onChanged: () => void;
  /** The browser's reader, handed in so a suite can stand in for the phone. */
  reader?: GeolocationReader | null;
}

export function LocationPinPanel({ locationId, facts, zone, dateFormat, apiBaseUrl = '', editable, onChanged, reader }: LocationPinPanelProps) {
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);

  const pinHere = useCallback(async () => {
    setPending(true);
    setMessage(null);
    const answer = await readPosition(reader === undefined ? browserGeolocation() : reader);
    if (!answer.ok) {
      // 🔨 A refusal is named and nothing is written (decision 0674).
      setMessage({ tone: 'error', text: `${t('location.pin.notPinned')} ${t(GEOLOCATION_REFUSAL_KEYS[answer.refusal])}` });
      setPending(false);
      return;
    }
    const written = await post(locationPinPath(locationId), answer.point, apiBaseUrl);
    setPending(false);
    if (!written.ok) {
      setMessage({ tone: 'error', text: written.message });
      return;
    }
    setMessage({ tone: 'good', text: t('location.pin.saved') });
    onChanged();
  }, [apiBaseUrl, locationId, onChanged, reader]);

  const clearPin = useCallback(async () => {
    const written = await del(locationPinPath(locationId), apiBaseUrl);
    if (!written.ok) {
      setMessage({ tone: 'error', text: written.message });
      return;
    }
    setMessage({ tone: 'good', text: t('location.pin.cleared') });
    onChanged();
  }, [apiBaseUrl, locationId, onChanged]);

  const maps = mapsSearchHref(facts.pinMapsQuery);
  const when = whenText(facts.pinnedAt, zone, dateFormat);
  return (
    <div className={styles.verify} data-location-pin={locationId}>
      <span className={styles.verifyTitle}>{t('location.pin.title')}</span>
      {facts.pinLine ? (
        <p className={styles.pinLine} data-location-pin-line={facts.pinLine}>
          <span>{facts.pinLine}</span>
          {facts.pinnedBy ? <span className={styles.municipality}>{[t('location.pin.by'), facts.pinnedBy, when].filter(Boolean).join(' ')}</span> : null}
          {/* 🔒 A pin read from a pasted link says so, and the link opens what was sent (decision 1600). */}
          {facts.pinnedFromLink ? (
            <a className={styles.municipality} href={facts.pinnedFromLink} target="_blank" rel="noopener noreferrer" data-location-pin-link="true">
              {t('location.pin.fromLink')}
            </a>
          ) : null}
          {maps ? (
            <a href={maps} target="_blank" rel="noopener noreferrer" data-location-pin-maps="true">
              {t('record.tile.openInMaps')}
            </a>
          ) : null}
        </p>
      ) : (
        <p className={styles.hint}>{t('location.pin.none')}</p>
      )}
      {editable ? (
        <div className={styles.actions}>
          <Button size={Size.Small} color={Color.Inactive} text={[pending ? t('location.pin.reading') : t('location.pin.here')]} actions={[() => void pinHere()]} disabled={pending} />
          {facts.pinLine ? <Button size={Size.Small} color={Color.Inactive} text={[t('location.pin.clear')]} actions={[() => void clearPin()]} /> : null}
        </div>
      ) : null}
      {message ? (
        <p className={message.tone === 'error' ? styles.verifyFailed : styles.hint} role="status" data-location-pin-message={message.tone}>
          {message.text}
        </p>
      ) : null}
    </div>
  );
}
